import "@finn-no/favorite-heart-component/dist/style.css";
import { i18n } from "@lingui/core";
import { WIcon } from "@warp-ds/components/react/icon";
import React, { CSSProperties, useCallback, useMemo, useState } from "react";
import { Recommendation } from "./components/Recommendation.js";
import { useTrackVisibility } from "./hooks/trackVisibility.js";
import { useExternalProps } from "./providers/ExternalPropsProvider.js";
import { useRecommendations } from "./providers/RecommendationsProvider.js";

export const Pagination = () => {
	const [showBackButton, setShowBackButton] = useState(false);
	const [showForwardButton, setShowForwardButton] = useState(true);
	const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
	const { locale } = useExternalProps();

	const { items, isPersonal } = useRecommendations();
	const filteredRecommendations = useMemo(() => items.filter(({ price }) => !!price) || [], [items]);

	useTrackVisibility(filteredRecommendations);

	const isTouchDevice = window?.matchMedia("(pointer: coarse)").matches;

	const handleScroll = useCallback((container: HTMLDivElement) => {
		const isNearStart = container.scrollLeft > 25;
		const isNearEnd = container.scrollWidth - container.offsetWidth - container.scrollLeft > 25;

		setShowBackButton(isNearStart);
		setShowForwardButton(isNearEnd);
	}, []);

	const scrollContainerRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node) {
				setScrollContainer(node); // Store the node in state for later use
				node.addEventListener("scroll", () => handleScroll(node));
				handleScroll(node); // Initialize button states on mount
			}
		},
		[handleScroll],
	);

	const scrollList = (direction: "forward" | "backward") => {
		if (scrollContainer) {
			const cardWidth = 304;
			const gap = 16;
			const step = cardWidth + gap;
			const visibleCards = Math.floor(scrollContainer.offsetWidth / step);
			const scrollAmount = Math.max(1, visibleCards) * step;
			const currentScroll = scrollContainer.scrollLeft;
			const newScrollPosition = direction === "forward" ? currentScroll + scrollAmount : currentScroll - scrollAmount;

			scrollContainer.scrollTo({ left: newScrollPosition, behavior: "smooth" });
		}
	};

	const headingTextRecommendationsForYou = i18n.t({
		id: "mobility-recommendations-title",
		message: "Recommendations for you",
	});
	const headingTextPopularAds = i18n.t({
		id: "mobility-recommendations-title.popular",
		message: "Popular ads",
	});

	if (!filteredRecommendations?.length) {
		return null;
	}

	return (
		<section
			className="flex flex-col gap-16 relative max-w-[1010]"
			style={
				{
					"--hover": "#323235",
					"--active": "#49494C",
				} as CSSProperties
			}
		>
			<h3 className="t3 mb-0">{isPersonal ? headingTextRecommendationsForYou : headingTextPopularAds}</h3>
			<div
				className="flex gap-16 overflow-x-auto overflow-y-hidden rounded-4 -mr-16 md:mx-0 pl-16 pr-16 md:px-10"
				ref={scrollContainerRef}
				style={{ scrollbarWidth: "none", scrollSnapType: "x mandatory" }}
			>
				{filteredRecommendations.map((recommendation) => {
					return <Recommendation key={recommendation.itemId} recommendation={recommendation} />;
				})}
			</div>
			{!isTouchDevice && (
				<button
					className={`absolute -left-24 top-[140] s-bg-inverted hover:bg-[--hover] active:bg-[--active] rounded-full w-48 h-48 flex items-center justify-center shadow-m z-10 transition-all ${!showBackButton ? "opacity-0 pointer-events-none" : "opacity-100"}`}
					onClick={() => scrollList("backward")}
				>
					<WIcon name="ChevronLeft" size="24px" className="s-icon-inverted" />
				</button>
			)}
			{!isTouchDevice && filteredRecommendations.length > 3 && (
				<button
					className={`absolute -right-24 top-[140] s-bg-inverted hover:bg-[--hover] active:bg-[--active] rounded-full w-48 h-48 flex items-center justify-center shadow-m z-10 transition-all ${!showForwardButton ? "opacity-0 pointer-events-none" : "opacity-100"}`}
					onClick={() => scrollList("forward")}
				>
					<WIcon name="ChevronRight" size="24px" locale={locale} className="s-icon-inverted" />
				</button>
			)}
		</section>
	);
};
